const PLANS = [
  {
    name: "Basic",
    subtitle: "Maintenance Cleaning",
    tagline: "Ideal for homes that are already clean and need only upkeep",
    priceLabel: "$150",
    startingAt: true,
    color: "border-stone-200",
    badge: null,
    features: [
      "General surface cleaning",
      "Sweeping, vacuuming & mopping",
      "Kitchen cleaning (exterior)",
      "Bathroom cleaning (general)",
      "Trash removal",
      "Light organization",
    ],
    recommendation: null,
    recommendationHot: false,
    noDeepClean: true,
    cta: "Get Started",
    ctaStyle: "border border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white",
  },
  {
    name: "Standard",
    subtitle: "Full Cleaning",
    tagline: "For maintaining your home in great condition with more attention to detail",
    priceLabel: "$220 – $250",
    startingAt: true,
    color: "border-teal-500",
    badge: "Most Popular",
    features: [
      "Everything in Basic",
      "More detailed bathroom cleaning",
      "Exterior cleaning of appliances",
      "Cleaning of doors and frames",
      "Baseboards",
      "More thorough dusting",
      "Inside the microwave",
    ],
    recommendation: "Recommended every Week or Bi-Weekly",
    recommendationHot: false,
    noDeepClean: false,
    cta: "Book Now",
    ctaStyle: "bg-teal-600 text-white hover:bg-teal-700",
  },
  {
    name: "Premium",
    subtitle: "Deep Cleaning",
    tagline: "Ideal for first-time cleanings or very dirty homes",
    priceLabel: "$300",
    startingAt: true,
    color: "border-stone-200",
    badge: null,
    features: [
      "Everything in Standard",
      "Deep cleaning of the entire home",
      "Inside oven and refrigerator",
      "Interior windows",
      "Cleaning of corners and hard-to-reach areas",
      "Removal of built-up grease",
      "Treatment of heavily soiled areas",
      "Special focus on kitchen and bathrooms",
    ],
    recommendation: "Recommended as an initial cleaning",
    recommendationHot: true,
    noDeepClean: false,
    cta: "Go Premium",
    ctaStyle: "border border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white",
  },
];

const EXTRAS = [
  { icon: "ri-bear-smile-line", label: "Homes with pets", note: "Additional charge" },
  { icon: "ri-brush-line", label: "Very dirty homes", note: "Additional charge" },
  { icon: "ri-hotel-bed-line", label: "Bed sheet change", note: "Add-on service" },
  { icon: "ri-t-shirt-line", label: "Laundry service", note: "Add-on service" },
  { icon: "ri-window-line", label: "Additional windows", note: "Add-on service" },
];

const ORG_AREAS = ["Closets", "Kitchens", "Pantries", "Bathrooms", "Kids\' rooms", "Play areas", "Drawers & storage spaces"];

const ORG_INCLUDES = [
  { icon: "ri-archive-line", text: "Sorting (keep, donate, discard)" },
  { icon: "ri-stack-line", text: "Folding clothes" },
  { icon: "ri-layout-grid-line", text: "Space optimization" },
  { icon: "ri-refresh-line", text: "Functional reorganization" },
  { icon: "ri-calendar-check-line", text: "Easy-to-maintain system" },
];

const ORG_IMPORTANT = [
  "Does not include organizing products (bins, boxes, etc.)",
  "May require additional time",
  "A prior evaluation is recommended for large projects",
];

export default function Pricing() {
  const scrollToBooking = () => {
    document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="pricing" className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-3 block">
            Simple Pricing
          </span>
          <h2
            className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Transparent &amp; Affordable
          </h2>
          <p className="text-gray-500 text-sm max-w-md mx-auto">
            No hidden fees. Pick the plan that suits your home.
          </p>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((plan) => (
            <div
              key={plan.name}
              className={`relative bg-white rounded-2xl border-2 ${plan.color} p-8 flex flex-col`}
            >
              {plan.badge && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-teal-600 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                  {plan.badge}
                </span>
              )}

              <div className="mb-6">
                <div className="flex items-baseline gap-2 mb-1">
                  <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {plan.name}
                  </h3>
                  {plan.subtitle && (
                    <span className="text-xs font-semibold text-teal-600 uppercase tracking-wide">
                      – {plan.subtitle}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400">{plan.tagline}</p>
              </div>

              <div className="mb-8">
                {plan.startingAt && (
                  <p className="text-xs text-gray-400 uppercase tracking-widest mb-1 font-medium">Starting at</p>
                )}
                <div className="flex items-end gap-2">
                  <span className="text-4xl font-extrabold text-gray-900 leading-none" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {plan.priceLabel}
                  </span>
                  <span className="text-gray-400 text-sm mb-1">/visit</span>
                </div>
              </div>

              <ul className="flex flex-col gap-3 mb-4 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-sm text-gray-600">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-base" />
                    </div>
                    {f}
                  </li>
                ))}
              </ul>

              {plan.noDeepClean && (
                <div className="flex items-start gap-2 mt-4 mb-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                    <i className="ri-information-line text-amber-500 text-sm" />
                  </div>
                  <p className="text-xs text-amber-700 font-medium leading-snug">
                    Does not include deep cleaning services.
                  </p>
                </div>
              )}

              {plan.recommendation && (
                <div className={`flex items-center gap-2 mt-4 mb-4 rounded-lg px-3 py-2 border ${
                  plan.recommendationHot ? "bg-orange-50 border-orange-200" : "bg-teal-50 border-teal-100"
                }`}>
                  <div className="w-4 h-4 flex items-center justify-center">
                    {plan.recommendationHot
                      ? <i className="ri-fire-line text-orange-500 text-sm" />
                      : <i className="ri-calendar-check-line text-teal-500 text-sm" />
                    }
                  </div>
                  <p className={`text-xs font-semibold leading-snug ${plan.recommendationHot ? "text-orange-700" : "text-teal-700"}`}>
                    {plan.recommendation}
                  </p>
                </div>
              )}

              <div className="mt-4">
                <button
                  onClick={scrollToBooking}
                  className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap ${plan.ctaStyle}`}
                >
                  {plan.cta}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* New Client Callout */}
        <div className="mt-8 flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-2xl px-6 py-4">
          <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
            <i className="ri-lightbulb-flash-line text-orange-500 text-lg" />
          </div>
          <p className="text-sm text-orange-800 font-medium leading-relaxed">
            For new clients or homes in poor condition, starting with a deep cleaning <strong>(Premium)</strong> is recommended.
          </p>
        </div>

        {/* Extras Section */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <span className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-2 block">Add-Ons</span>
            <h3 className="text-2xl font-extrabold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Extras &amp; Special Charges
            </h3>
            <p className="text-sm text-gray-400 mt-2">Customizable additions to any cleaning package.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {EXTRAS.map((extra) => (
              <div key={extra.label} className="bg-white border border-stone-200 rounded-xl p-4 flex flex-col items-center text-center gap-2">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-full">
                  <i className={`${extra.icon} text-teal-600 text-xl`} />
                </div>
                <p className="text-sm font-semibold text-gray-800 leading-snug">{extra.label}</p>
                <span className="text-xs text-teal-600 font-medium bg-teal-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                  {extra.note}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Organization Add-On Section */}
        <div className="mt-16 bg-white border-2 border-stone-200 rounded-3xl overflow-hidden">
          {/* Header Banner */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-500 px-8 py-6">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs font-bold uppercase tracking-widest text-teal-100">Add-On Service</span>
            </div>
            <h3 className="text-2xl font-extrabold text-white mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Space Organization
            </h3>
            <p className="text-teal-100 text-sm">
              Enhance your cleaning service with professional organization
            </p>
          </div>

          <div className="p-8 grid md:grid-cols-2 gap-10">
            {/* Left column */}
            <div className="flex flex-col gap-8">

              {/* How it works */}
              <div>
                <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-4">How does it work?</h4>
                <ol className="flex flex-col gap-3">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-teal-50 text-teal-700 text-xs font-bold shrink-0 mt-0.5">1</span>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Choose your cleaning package</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-xs px-2.5 py-1 rounded-full bg-stone-100 text-stone-600 font-medium whitespace-nowrap">Basic</span>
                        <span className="text-xs px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 font-medium whitespace-nowrap">Standard</span>
                        <span className="text-xs px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 font-medium whitespace-nowrap">Premium</span>
                      </div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-teal-50 text-teal-700 text-xs font-bold shrink-0 mt-0.5">2</span>
                    <p className="text-sm text-gray-600">Add the organization service based on your needs</p>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-teal-50 text-teal-700 text-xs font-bold shrink-0 mt-0.5">3</span>
                    <p className="text-sm text-gray-600">Customize your service to fit you</p>
                  </li>
                </ol>
              </div>

              {/* Areas */}
              <div>
                <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-4">What areas can you add?</h4>
                <div className="flex flex-wrap gap-2">
                  {ORG_AREAS.map((area) => (
                    <span key={area} className="text-xs px-3 py-1.5 rounded-full border border-stone-200 bg-stone-50 text-stone-700 font-medium whitespace-nowrap">
                      {area}
                    </span>
                  ))}
                </div>
              </div>

              {/* Pricing */}
              <div>
                <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-4">Pricing</h4>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-3 bg-teal-50 rounded-xl px-4 py-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-time-line text-teal-600 text-base" />
                    </div>
                    <span className="text-sm font-bold text-teal-800">$40 – $50</span>
                    <span className="text-xs text-teal-600">per hour</span>
                  </div>
                  <div className="flex items-center gap-3 bg-stone-50 rounded-xl px-4 py-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-price-tag-3-line text-stone-500 text-base" />
                    </div>
                    <span className="text-sm font-bold text-stone-700">Custom pricing</span>
                    <span className="text-xs text-stone-500">per area</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="flex flex-col gap-8">

              {/* What's included */}
              <div>
                <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-4">What&apos;s included?</h4>
                <ul className="flex flex-col gap-3">
                  {ORG_INCLUDES.map((item) => (
                    <li key={item.text} className="flex items-center gap-3 text-sm text-gray-600">
                      <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg shrink-0">
                        <i className={`${item.icon} text-teal-600 text-base`} />
                      </div>
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Key Benefit */}
              <div className="bg-teal-600 rounded-2xl px-6 py-5">
                <p className="text-xs font-bold uppercase tracking-widest text-teal-200 mb-2">Key Benefit</p>
                <p className="text-white text-sm font-medium leading-relaxed">
                  We don&apos;t just leave your home clean — we leave it <strong>organized so it stays that way every day.</strong>
                </p>
              </div>

              {/* Important notes */}
              <div>
                <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-4">Important</h4>
                <ul className="flex flex-col gap-2.5">
                  {ORG_IMPORTANT.map((note) => (
                    <li key={note} className="flex items-start gap-2.5 text-sm text-gray-500">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-information-line text-amber-500 text-sm" />
                      </div>
                      {note}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* CTA Strip */}
          <div className="border-t border-stone-100 px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4 bg-stone-50">
            <p className="text-sm text-gray-500">
              Ready to add organization to your cleaning service?
            </p>
            <button
              onClick={scrollToBooking}
              className="bg-teal-600 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Add Organization to My Booking
            </button>
          </div>
        </div>

        {/* Bottom Note */}
        <p className="text-center text-xs text-gray-400 mt-10">
          All plans include our 100% satisfaction guarantee. Not happy? We&apos;ll re-clean for free.
        </p>
      </div>
    </section>
  );
}
